import { Grid, Link } from "@mui/material";
import React, { FC, useMemo } from "react";
import DividerWithText from "./DividerWithText";
import useAuthentication from "../hooks/useAuthentication";
import useAuthorization from "../hooks/useAuthorization";
import useIsPortal from "../hooks/useIsPortal";

const FORM_WIDTH = 250;

type HomeAdministrationProps = {
  showAdminArea?: boolean;
  portalMode: boolean;
  onEditAccountClick: () => void;
};

const HomeAdministration: FC<HomeAdministrationProps> = ({
  showAdminArea,
  portalMode,
  onEditAccountClick,
}) => {
  // in order for the WebMap link and text to be meaningful,
  // we need to check whether the user is viewing this component
  // within the portal or a webmap. If inside a webmap, the user (if their role
  // is "Creator") can only edit the current webmap, not multiple ones.
  const webMapLinkProps = useMemo(() => {
    if (portalMode) {
      return { href: "/content/tables/webmaps", text: "WebMaps" };
    }

    return { href: "/content/tables/webmap", text: "WebMap" };
  }, [portalMode]);

  return (
    <Grid
      container
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
    >
      <Grid item sx={{ mt: 2 }} width={FORM_WIDTH * 1.5}>
        <DividerWithText>Administration</DividerWithText>
      </Grid>
      {showAdminArea && (
        <>
          <Grid item>
            <Link href="/content/tables/seclevels">Security Level</Link>
          </Grid>
          <Grid item>
            <Link href="/content/tables/accounts">Nutzer:innen</Link>
          </Grid>
          <Grid item sx={{ mb: 2 }}>
            <Link href={webMapLinkProps.href}>{webMapLinkProps.text}</Link>
          </Grid>
        </>
      )}
      <Grid item>
        <Link component={"button"} onClick={onEditAccountClick}>
          Accountdaten
        </Link>
      </Grid>
    </Grid>
  );
};

/**
 * Can only use role and secLevel information in WebMap, where we have access to the useAuthorization
 * context, so we wrap it in a component where we can assume safe access to authz context.
 */
export const HomeAdministrationWebMap: FC<
  Omit<HomeAdministrationProps, "portalMode">
> = ({ showAdminArea, onEditAccountClick }) => {
  const { user } = useAuthentication();
  const { rolle } = useAuthorization();

  return (
    <HomeAdministration
      showAdminArea={
        showAdminArea !== undefined
          ? showAdminArea
          : user?.admin
          ? true
          : rolle === "Creator"
      }
      portalMode={false}
      onEditAccountClick={onEditAccountClick}
    />
  );
};

export const HomeAdministrationWithContext: FC<
  Omit<HomeAdministrationProps, "portalMode">
> = ({ showAdminArea, onEditAccountClick }) => {
  const { user } = useAuthentication();
  const { isPortal } = useIsPortal();

  if (!isPortal) {
    return (
      <HomeAdministrationWebMap
        showAdminArea={showAdminArea}
        onEditAccountClick={onEditAccountClick}
      />
    );
  }

  return (
    <HomeAdministration
      showAdminArea={showAdminArea !== undefined ? showAdminArea : user?.admin}
      portalMode={true}
      onEditAccountClick={onEditAccountClick}
    />
  );
};

export default HomeAdministration;
